import {
  AddOutlined,
  ArrowBackOutlined,
  DeleteOutlined,
  EditOutlined,
  InventoryOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import type {
  IBrandModel,
  ICategory,
  PaginatedResponse,
} from "../../../types/equipment";
import AnimatedTable, { ColumnDef } from "./AnimatedTable";
import EditCatalogDialog from "./EditCatalogDialog";
import AddEquipmentDialog from "./AddEquipmentDialog";
import type { AddEquipmentFormData } from "../../../types/equipment";

const ROWS_PER_PAGE = 12;

const ListCatalog: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [brandModels, setBrandModels] = useState<IBrandModel[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [filterCategory, setFilterCategory] = useState("all");
  const [editingBrandModel, setEditingBrandModel] =
    useState<IBrandModel | null>(null);
  const [editCatalogOpen, setEditCatalogOpen] = useState(false);
  const [addAssetOpen, setAddAssetOpen] = useState(false);
  const [addingForBrandModel, setAddingForBrandModel] =
    useState<IBrandModel | null>(null);
  const [deleting, setDeleting] = useState<number | string | null>(null);

  const fetchBrandModels = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiService.get<PaginatedResponse<IBrandModel>>(
        "/api/equipments/brand-model/",
      );
      setBrandModels(res.data.results || []);
    } catch {
      setError("Failed to load catalog.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await apiService.get<PaginatedResponse<ICategory>>(
        "/api/equipments/categories/",
      );
      setCategories(res.data.results || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchBrandModels();
    fetchCategories();
  }, [fetchBrandModels, fetchCategories]);

  const handleAddAsset = async (data: AddEquipmentFormData) => {
    if (!addingForBrandModel) return;
    try {
      await apiService.post("/api/equipments/equipment/", {
        brand_model_id: addingForBrandModel.id,
        serial_number: data.unit.serial_number,
        asset_tag: data.unit.asset_tag || undefined,
        status: data.unit.status || "available",
        condition: data.unit.condition || "good",
        location: data.unit.location || undefined,
        purchase_date: data.unit.purchase_date || undefined,
        warranty_expiry_date: data.unit.warranty_expiry_date || undefined,
        purchase_cost: data.unit.purchase_cost || undefined,
        current_value: data.unit.current_value || undefined,
        supplier_name: data.unit.supplier_name || undefined,
        contact_name: data.unit.contact_name || undefined,
        supplier_phone: data.unit.supplier_phone || undefined,
        supplier_email: data.unit.supplier_email || undefined,
        supplier_website: data.unit.supplier_website || undefined,
      });
      setAddAssetOpen(false);
      setAddingForBrandModel(null);
      await fetchBrandModels();
    } catch (err: unknown) {
      const flat = (o: unknown): string[] => {
        if (typeof o === "string") return [o];
        if (Array.isArray(o)) return o.flatMap(flat);
        if (o && typeof o === "object")
          return Object.values(o as Record<string, unknown>).flatMap(flat);
        return [];
      };
      const msgs = flat(
        (err as { response?: { data?: unknown } })?.response?.data,
      );
      setError(msgs.length ? msgs.join(" · ") : "Failed to add unit.");
    }
  };

  const handleDeleteBrandModel = async (
    bm: IBrandModel,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    if (
      !window.confirm(
        `Delete "${bm.brand_name ? `${bm.brand_name} ${bm.model}` : bm.model}"? This cannot be undone.`,
      )
    )
      return;
    try {
      setDeleting(bm.id);
      await apiService.delete(`/api/equipments/brand-model/${bm.id}/`);
      await fetchBrandModels();
    } catch {
      setError("Failed to delete catalog entry.");
    } finally {
      setDeleting(null);
    }
  };

  const getCategoryName = (bm: IBrandModel) =>
    bm.category && typeof bm.category === "object"
      ? (bm.category as ICategory).name
      : bm.category_name || "—";

  const filtered = brandModels.filter((bm) => {
    if (filterCategory === "all") return true;
    return getCategoryName(bm) === filterCategory;
  });

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paginated = filtered.slice(
    page * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE + ROWS_PER_PAGE,
  );

  const columns: ColumnDef<IBrandModel>[] = [
    {
      key: "name",
      label: "Model",
      cellSx: { fontWeight: 500 },
      render: (bm) => (
        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {bm.brand_name ? `${bm.brand_name} ${bm.model}` : bm.model}
          </Typography>
          {bm.manufacturer && (
            <Typography
              sx={{ fontSize: 11, color: theme.palette.text.secondary }}
            >
              {bm.manufacturer}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "category",
      label: "Category",
      render: (bm) => (
        <Chip
          label={getCategoryName(bm)}
          size="small"
          sx={{
            fontSize: 11,
            height: 22,
            borderRadius: "10px",
            backgroundColor: colors.primary[800],
            color: theme.palette.text.secondary,
            fontWeight: 500,
          }}
        />
      ),
    },
    {
      key: "total",
      label: "Total",
      render: (bm) => (
        <Typography
          sx={{
            fontSize: 13,
            color: theme.palette.text.primary,
            fontWeight: 600,
          }}
        >
          {bm.brand_model_quantity ?? 0}
        </Typography>
      ),
    },
    {
      key: "available",
      label: "Available",
      render: (bm) => {
        const available = bm.available_quantity ?? 0;
        const total = bm.brand_model_quantity ?? 0;
        const color =
          available === 0
            ? "#ef4444"
            : available < total
              ? "#ffc107"
              : "#4caf7d";
        return (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color }}>
            {available}
          </Typography>
        );
      },
    },
    {
      key: "allocated",
      label: "Allocated",
      render: (bm) => (
        <Typography sx={{ fontSize: 13, color: "#4285f4" }}>
          {bm.allocated_quantity ?? 0}
        </Typography>
      ),
    },
    {
      key: "rented",
      label: "Rented",
      render: (bm) => (
        <Typography sx={{ fontSize: 13, color: "#f59e0b" }}>
          {bm.rented_quantity ?? 0}
        </Typography>
      ),
    },
    {
      key: "maintenance",
      label: "Maintenance",
      render: (bm) => (
        <Typography sx={{ fontSize: 13, color: "#ef4444" }}>
          {bm.maintenance_quantity ?? 0}
        </Typography>
      ),
    },
    {
      key: "rental_value",
      label: "Daily Rate",
      render: (bm) =>
        bm.rental_value ? (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#10b981" }}>
            ${bm.rental_value}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled }}>
            —
          </Typography>
        ),
    },
    {
      key: "action",
      label: "Action",
      align: "center",
      render: (bm) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
          <Tooltip title="Add unit" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setAddingForBrandModel(bm);
                setAddAssetOpen(true);
              }}
            >
              <AddOutlined
                sx={{ fontSize: 16, color: theme.palette.text.secondary }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditingBrandModel(bm);
                setEditCatalogOpen(true);
              }}
            >
              <EditOutlined
                sx={{ fontSize: 16, color: theme.palette.text.secondary }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              disabled={deleting === bm.id}
              onClick={(e) => handleDeleteBrandModel(bm, e)}
            >
              {deleting === bm.id ? (
                <CircularProgress size={14} />
              ) : (
                <DeleteOutlined sx={{ fontSize: 16, color: "#ef4444" }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const filterSelectSx = {
    fontSize: 14,
    fontWeight: 500,
    height: 40,
    borderRadius: "12px",
    backgroundColor: colors.primary[900],
    color: theme.palette.text.primary,
    minWidth: 160,
    "& .MuiSelect-icon": { color: theme.palette.text.primary },
  };

  return (
    <Box sx={{ p: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
          <IconButton
            onClick={() => navigate("/gear-management")}
            sx={{ bgcolor: colors.primary[800] }}
          >
            <ArrowBackOutlined />
          </IconButton>
          <Typography
            variant="h5"
            fontWeight="bold"
            color={theme.palette.text.primary}
          >
            Catalog
          </Typography>
        </Box>
        <Typography variant="body2" color={theme.palette.text.secondary} mb={3}>
          Equipment models and stock overview
        </Typography>

        <Box
          sx={{
            backgroundColor: colors.primary[900],
            borderRadius: "16px",
            p: 4,
            minHeight: "70vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <FormControl size="small">
              <Select
                value={filterCategory}
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setPage(0);
                }}
                sx={filterSelectSx}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.name}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={() => {
                setAddingForBrandModel(null);
                setAddAssetOpen(true);
              }}
              sx={{
                fontSize: 14,
                fontWeight: 500,
                px: 3,
                height: 40,
                borderRadius: "10px",
              }}
            >
              Add Model
            </Button>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
              <CircularProgress size={32} sx={{ color: colors.primary[400] }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <InventoryOutlined
                sx={{ fontSize: 36, color: colors.primary[600], mb: 1 }}
              />
              <Typography color="text.secondary">
                No catalog entries found.
              </Typography>
            </Box>
          ) : (
            <AnimatedTable
              columns={columns}
              rows={paginated}
              getRowId={(bm) => bm.id.toString()}
              onRowClick={(bm) => navigate(`/gear-management/catalog/${bm.id}`)}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              emptyMessage="No catalog entries found."
            />
          )}
        </Box>
      </motion.div>

      {editingBrandModel && (
        <EditCatalogDialog
          open={editCatalogOpen}
          onClose={() => {
            setEditCatalogOpen(false);
            setEditingBrandModel(null);
          }}
          brandModel={editingBrandModel}
          onSaved={() => {
            setEditCatalogOpen(false);
            setEditingBrandModel(null);
            fetchBrandModels();
          }}
        />
      )}

      <AddEquipmentDialog
        open={addAssetOpen}
        onClose={() => {
          setAddAssetOpen(false);
          setAddingForBrandModel(null);
        }}
        onSubmit={handleAddAsset}
        equipment={null}
        isEdit={false}
      />
    </Box>
  );
};

export default ListCatalog;
